// Core packages
import { Component, Inject, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';

// Third party packages
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import {
  AlignmentType, Document, HeadingLevel, Media, Packer, PageOrientation, Paragraph, Table, TableCell, TableRow, TextRun
} from "docx";
import * as FileSaver from "file-saver";
import { MatProgressButtonOptions } from 'mat-progress-buttons';
import moment from "moment";
import { CookieService } from 'ngx-cookie-service';
import { Subscription } from "rxjs";

// Custom packages
import { DocumentArchivesService } from "src/app/modules/properties/document-archives.service";
import { PropertiesService } from "src/app/modules/properties/properties.service";
import { SettingsService } from "src/app/modules/settings/settings.service";
import { PropertyInterface } from "src/app/shared/models/property/property.interface";
import { Property } from "src/app/shared/models/property/property.model";
import { ConfigService } from 'src/app/shared/services/config.service';
import { HelperService } from "src/app/shared/services/helper.service";
import DocumentTypeStructure from "../../documentTypes";

@Component({
  selector: 'app-export-contracts-dialog',
  templateUrl: './export-contracts-dialog.component.html',
  styleUrls: ['./export-contracts-dialog.component.scss']
})
export class ExportContractsDialogComponent implements OnInit {
  lang = this.translateService.currentLang || this.translateService.defaultLang;

  private subscriptions: Subscription[] = [];

  // BEGIN - Add subUser form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly

  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant('common.cta.send'),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: 'primary',
    spinnerColor: 'primary',
    fullWidth: false,
    disabled: false,
    mode: 'indeterminate',
    buttonIcon: {
      fontSet: 'fal',
      fontIcon: 'fa-paper-plane',
      inline: true,
    },
  };
  // END - Add communication form

  dialogIsLoading = true;

  public property: Property = new Property();
  propertyData: PropertyInterface;
  propertyName = "";
  subscriptionType = "FULL";
  documentArchiveId: string;
  public documentTypeStructure: DocumentTypeStructure =
    new DocumentTypeStructure();
  documentTemplateId = "";
  documentTemplate: any[] = [];
  documentArchive: any[] = [];

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<ExportContractsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public configService: ConfigService,

    private cookieService: CookieService,
    private translateService: TranslateService,
    private propertiesService: PropertiesService,
    private helperService: HelperService,
    private documentArchivesService: DocumentArchivesService,
    private settingsService: SettingsService,
  ) { }

  /**
   * Init component
   *
   */
  ngOnInit(): void {
    // Get current lang
    this.lang = this.cookieService.get('lang') || this.configService.defaultLang;

    const host = `${this.configService.frontEndHost}`;

    this.subscriptions.push(
      this.propertiesService.get(this.data.propertyId).subscribe(
        (res) => {
          if (res.status) {
            this.property.setData(res.data);
            this.propertyData = res.data;
            this.propertyName = res.data.name;

            if (
              this.propertyData &&
              this.propertyData.subscription &&
              this.propertyData.subscription.key
            ) {
              this.subscriptionType = this.propertyData.subscription.key;

              if (this.propertyData.documentArchive) {
                this.documentArchiveId = this.propertyData.documentArchive;
              }
            }

            // If property has a structure ID, than retrive related data
            // and document archive
            if (
              this.propertyData.structure
            ) {
              this.subscriptions.push(
                this.settingsService.getDocument().subscribe(
                  (res) => {
                    if (res.status) {
                      if (
                        res.data &&
                        Array.isArray(res.data) &&
                        res.data.length > 0 &&
                        res.data[0].area &&
                        Array.isArray(res.data[0].area)
                      ) {
                        this.documentTemplateId = res.data[0]._id;
                        this.documentTemplate = res.data[0].area;
                      }

                      this.documentArchivesService
                        .getContracts(this.property.get("documentArchive"))
                        .subscribe((res) => {
                          if (
                            res.status &&
                            res.data &&
                            Array.isArray(res.data)
                          ) {
                            for (const [i, element] of res.data.entries()) {
                              element.index = i;
                              const documentKey = element.key
                              const section = this.documentTypeStructure.getDocRoute2(documentKey, this.documentTemplate);

                              let documentId = 'generic';
                              if (
                                element.structureObjectId !== '' &&
                                element.structureObjectId !== '-1'
                              ) {
                                documentId = element.docId;
                              }
                              const subDocumentId = element.subDocId

                              element.qrcode =
                                `${host}/qrcode/${this.documentArchiveId}/${section}/${documentId}/${documentKey}/${subDocumentId}`;

                              if (!element.deleted && !element.expired) {
                                this.documentArchive.push(element);
                              }
                            }
                            this.dialogIsLoading = false;
                          } else {
                            this.dialogIsLoading = false;
                          }
                        });
                      
                    } else {
                      this.dialogIsLoading = false;
                    }
                  },
                  (err) => {
                    this.helperService.handleError(err);
                    this.dialogIsLoading = false;
                  }
                )
              );
            } else {
              this.dialogIsLoading = false;
            }
          }
        },
        (err) => {
          this.dialogIsLoading = false;
          this.helperService.handleError(err);
        }
      )
    );
  }


  /**
   * Export word file
   *
   * @since 1.0.0
   */
  onExportFile(): void {
    // Create document data
    const doc = new Document();

    const rows: any[] = [];
    rows.push(
      new TableRow({
        tableHeader: true,
        children: [
          new TableCell({
            children: [
              new Paragraph({
                text: this.translateService.instant('properties.documentsPage.docType').toUpperCase(),
                heading: HeadingLevel.HEADING_2,
                alignment: AlignmentType.CENTER,
              }),
            ],
          }),
          new TableCell({
            children: [
              new Paragraph({
                text: this.translateService.instant('properties.structures.structure').toUpperCase(),
                heading: HeadingLevel.HEADING_2,
                alignment: AlignmentType.CENTER,
              }),
            ],
          }),
          new TableCell({
            children: [
              new Paragraph({
                text: this.translateService.instant('properties.documentsPage.supplier').toUpperCase(),
                heading: HeadingLevel.HEADING_2,
                alignment: AlignmentType.CENTER,
              }),
            ],
          }),
          new TableCell({
            children: [
              new Paragraph({
                text: this.translateService.instant('properties.documentsPage.date').toUpperCase(),
                heading: HeadingLevel.HEADING_2,
                alignment: AlignmentType.CENTER,
              }),
            ],
          }),
          new TableCell({
            children: [
              new Paragraph({
                text: this.translateService.instant('properties.documentsPage.expiredDate').toUpperCase(),
                heading: HeadingLevel.HEADING_2,
                alignment: AlignmentType.CENTER,
              }),
            ],
          }),
          new TableCell({
            children: [
              new Paragraph({
                text: this.translateService.instant('common.cta.printQr').toUpperCase(),
                heading: HeadingLevel.HEADING_2,
                alignment: AlignmentType.CENTER,
              }),
            ],
          }),
        ],
      })
    );

    for (const element of this.documentArchive) {
      const qrImage = document
        .getElementById(`qr-img-${element.index}`)
        .lastElementChild.lastElementChild.getAttribute("src");
      const image = Media.addImage(doc, qrImage, 105, 105, {
      });

      const row = new TableRow({
        children: [
          new TableCell({
            margins: {
              top: 60,
              left: 60,
              bottom: 60,
              right: 60,
            },
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: element.type,
                    size: 26,
                  })
                ]
              }),
            ],
          }),
          new TableCell({
            margins: {
              top: 60,
              left: 60,
              bottom: 60,
              right: 60,
            },
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: element.structureObjectName,
                    size: 26,
                  })
                ]
              }),
            ],
          }),
          new TableCell({
            margins: {
              top: 60,
              left: 60,
              bottom: 60,
              right: 60,
            },
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: element.data.ragioneSocialeFornitore,
                    size: 26,
                  })
                ]
              }),
            ],
          }),
          new TableCell({
            margins: {
              top: 60,
              left: 60,
              bottom: 60,
              right: 60,
            },
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: moment(element.data.dataDocumento).isValid() ? 
                      moment(element.data.dataDocumento).format("L") : 
                      element.data.dataDocumento,
                    size: 26,
                  })
                ]
              }),
            ],
          }),
          new TableCell({
            margins: {
              top: 60,
              left: 60,
              bottom: 60,
              right: 60,
            },
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: moment(element.dateExpired).isValid() ? 
                      moment(element.dateExpired).format("L") : 
                      element.dateExpired,
                    size: 26,
                  })
                ]
              }),
            ],
          }),
          new TableCell({
            margins: {
              top: 60,
              left: 60,
              bottom: 60,
              right: 60,
            },
            children: [
              new Paragraph({
                children: [
                  image
                ],
              }),
            ],
          }),
        ],
      });

      rows.push(row);

      const blankRow = new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                text: '',
              }),
            ],
          }),
          new TableCell({
            children: [
              new Paragraph({
                text: '',
              }),
            ],
          }),
          new TableCell({
            children: [
              new Paragraph({
                text: '',
              }),
            ],
          }),
          new TableCell({
            children: [
              new Paragraph({
                text: '',
              }),
            ],
          }),
          new TableCell({
            children: [
              new Paragraph({
                text: '',
              }),
            ],
          }),
          new TableCell({
            children: [
              new Paragraph({
                text: '',
              }),
            ],
          }),
        ],
      });
      rows.push(blankRow);
    }

    doc.addSection({
      properties: {
        orientation: PageOrientation.LANDSCAPE,
      },
      children: [
        new Paragraph({
          alignment: AlignmentType.CENTER,
          heading: HeadingLevel.HEADING_1,
          spacing: { after: 400 },
          children: [
            new TextRun({
              text: `CONDOMINIO ${this.property.get("name")}`,
              bold: true,
              allCaps: true,
              color: "auto",
              size: 24 * 2,
            }),
          ],
        }),
        // new Paragraph(image),
        new Table({
          rows: rows,
        }),
      ],
    });

    // Used to export the file into a .docx file
    Packer.toBlob(doc).then((blob) => {
      FileSaver.saveAs(blob, `Contratti_${this.property.get("name")}.docx`);
    });
  }

  /**
   * Handle dialog closing
   *
   * @since 1.0.0
   *
   */
  onClose(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());

    this.dialogRef.close();
  }
}
